import { useEffect, useMemo, useState } from 'react';
import { api, camposDoErro, mensagemDoErro, type Receita, type Template, type Canal } from '../api';
import { useApp } from '../estado';
import { useRecurso } from '../ganchos';
import {
  formatarDataHora,
  preencherModelo,
  variaveisExemplo,
} from '../formatacao';
import {
  Aviso,
  Campo,
  Carregando,
  EstadoVazio,
  Switch,
  TituloPagina,
} from '../componentes/ui';

interface Edicao {
  ativa: boolean;
  valores: Record<string, string>;
  templateId: string | null;
  canalId: string | null;
}

function valoresIniciais(receita: Receita): Record<string, string> {
  const valores: Record<string, string> = {};
  for (const parametro of receita.parametros) {
    const salvo = receita.valores?.[parametro.chave];
    valores[parametro.chave] =
      salvo === undefined || salvo === null ? String(parametro.padrao) : String(salvo);
  }
  return valores;
}

export function Receitas() {
  const { provedor, notificar } = useApp();
  const receitas = useRecurso(() => api.receitas(), []);
  const templates = useRecurso(() => api.templates(), []);
  const configuracoes = useRecurso(() => api.configuracoes(), []);

  const [edicoes, setEdicoes] = useState<Record<string, Edicao>>({});
  const [erros, setErros] = useState<Record<string, Record<string, string>>>({});
  const [salvando, setSalvando] = useState<string | null>(null);

  useEffect(() => {
    const lista = receitas.dados?.receitas;
    if (!lista) return;
    setEdicoes((atual) => {
      const proximo = { ...atual };
      for (const receita of lista) {
        if (!proximo[receita.id]) {
          proximo[receita.id] = {
            ativa: receita.ativa,
            valores: valoresIniciais(receita),
            templateId: receita.templateId,
            canalId: receita.canalId,
          };
        }
      }
      return proximo;
    });
  }, [receitas.dados]);

  const empresa = provedor?.branding.nomeExibicao ?? 'seu Provedor';
  const exemplo = useMemo(() => variaveisExemplo(empresa), [empresa]);
  const listaTemplates: Template[] = templates.dados?.templates ?? [];
  const listaCanais: Canal[] = configuracoes.dados?.canais ?? [];
  const ativas = receitas.dados?.receitas.filter((r) => edicoes[r.id]?.ativa).length ?? 0;

  const alterar = (tipo: string, mudanca: Partial<Edicao>) => {
    setEdicoes((atual) => {
      const base = atual[tipo];
      if (!base) return atual;
      return { ...atual, [tipo]: { ...base, ...mudanca } };
    });
  };

  const alterarValor = (tipo: string, chave: string, valor: string) => {
    setEdicoes((atual) => {
      const base = atual[tipo];
      if (!base) return atual;
      return { ...atual, [tipo]: { ...base, valores: { ...base.valores, [chave]: valor } } };
    });
  };

  const salvar = async (receita: Receita) => {
    const edicao = edicoes[receita.id];
    if (!edicao) return;

    const locais: Record<string, string> = {};
    for (const parametro of receita.parametros) {
      const bruto = (edicao.valores[parametro.chave] ?? '').trim();
      if (parametro.tipo === 'hora') {
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(bruto)) {
          locais[parametro.chave] = 'Informe um horário entre 00:00 e 23:59.';
        }
        continue;
      }
      if (parametro.chave === 'dias_antes' || parametro.chave === 'dias_apos') {
        const numero = Number(bruto);
        if (!Number.isInteger(numero) || numero < 1) {
          locais[parametro.chave] = 'Informe um número inteiro maior que zero.';
          continue;
        }
        if (parametro.maximo !== undefined && numero > parametro.maximo) {
          locais[parametro.chave] = `O máximo é ${parametro.maximo}.`;
        }
      }
      if (parametro.chave === 'dias_desbloqueio') {
        const numero = Number(bruto);
        if (!Number.isInteger(numero) || numero < 1 || numero > 7) {
          locais[parametro.chave] = 'Escolha de 1 a 7 dias.';
        }
      }
    }

    if (receita.exigeTemplate && edicao.ativa && !edicao.templateId) {
      locais.templateId = 'Escolha o template que o Titular vai receber.';
    }

    if (Object.keys(locais).length > 0) {
      setErros((atual) => ({ ...atual, [receita.id]: locais }));
      notificar('erro', 'Reveja os campos destacados antes de salvar.');
      return;
    }

    setSalvando(receita.id);
    try {
      await api.salvarReceita(receita.id, {
        ativa: edicao.ativa,
        parametros: edicao.valores,
        templateId: edicao.templateId,
        canalId: edicao.canalId,
      });
      setErros((atual) => ({ ...atual, [receita.id]: {} }));
      notificar('sucesso', `Receita "${receita.rotulo}" salva.`);
      await receitas.recarregar();
    } catch (falha) {
      const campos = camposDoErro(falha);
      if (Object.keys(campos).length > 0) {
        setErros((atual) => ({ ...atual, [receita.id]: campos }));
      }
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setSalvando(null);
    }
  };

  if (receitas.carregando && !receitas.dados) return <Carregando texto="Lendo as réguas do Provedor…" />;

  if (receitas.erro && !receitas.dados) {
    return (
      <Aviso tipo="erro" titulo="Não foi possível carregar as receitas">
        {receitas.erro}
      </Aviso>
    );
  }

  const definicoes = receitas.dados?.receitas ?? [];

  return (
    <>
      <TituloPagina
        titulo="Receitas"
        descricao="Cinco réguas fechadas, sem editor de nós. Cada uma tem gatilho, prazo e mensagem conhecidos — o Provedor só decide se liga, quando e com qual texto."
      />

      <Aviso tipo="info" titulo={`${ativas} de ${definicoes.length} receitas ligadas`}>
        Receita desligada não gera Disparo. Alterar o texto de um template vale para todos os
        Disparos futuros que o usam; o que já foi enviado não muda.
      </Aviso>

      {definicoes.length === 0 ? (
        <EstadoVazio
          titulo="Nenhuma receita disponível"
          texto="O catálogo de receitas vem do domínio do produto. Se esta lista estiver vazia, a API não respondeu como esperado."
          icone="◌"
        />
      ) : null}

      {definicoes.map((receita) => {
        const edicao = edicoes[receita.id];
        if (!edicao) return null;
        const errosDaReceita = erros[receita.id] ?? {};
        const templateSelecionado = listaTemplates.find((t) => t.id === edicao.templateId) ?? null;
        const corpoPrevia = templateSelecionado?.corpo ?? receita.corpoPadrao;
        const mensagem = `${preencherModelo(corpoPrevia, exemplo)}\n\n— ${empresa}\nResponda SAIR para não receber mais.`;

        return (
          <article
            className="receita"
            key={receita.id}
            data-ativa={edicao.ativa}
            aria-labelledby={`receita-${receita.id}`}
          >
            <header className="receita__cabecalho">
              <div className="receita__identidade">
                <div className="receita__nome">
                  <h2 id={`receita-${receita.id}`}>{receita.rotulo}</h2>
                  <span className={`badge ${edicao.ativa ? 'badge--ok' : 'badge--neutro'}`}>
                    <span aria-hidden="true">{edicao.ativa ? '✓' : '—'}</span>
                    {edicao.ativa ? 'Ligada' : 'Desligada'}
                  </span>
                  {receita.acaoErp ? (
                    <span className="badge badge--alerta">
                      <span aria-hidden="true">⚠</span> Escreve no ERP
                    </span>
                  ) : null}
                  <span className="badge badge--neutro">
                    {receita.categoria === 'cobranca' ? 'Cobrança' : 'Relacionamento'}
                  </span>
                </div>
                <p className="receita__resumo">{receita.resumo}</p>
                <ul className="receita__metadados">
                  <li>
                    <strong>Gatilho:</strong> {receita.gatilho}
                  </li>
                  <li>
                    <strong>O Titular recebe:</strong> {receita.acao}
                  </li>
                </ul>
              </div>

              <Switch
                rotulo={edicao.ativa ? 'Receita ligada' : 'Receita desligada'}
                checked={edicao.ativa}
                onChange={(valor) => alterar(receita.id, { ativa: valor })}
              />
            </header>

            {receita.acaoErp ? (
              <Aviso tipo="alerta" titulo="Ação no ERP">
                Esta receita pede desbloqueio em confiança ao ERP antes de avisar o Titular. O
                rebloqueio continua sendo do ERP — o motor nunca agenda rebloqueio.
              </Aviso>
            ) : null}

            <div className="receita__corpo">
              <div className="pilha">
                <div className="receita__form">
                  {receita.parametros.map((parametro) => (
                    <Campo
                      key={parametro.chave}
                      rotulo={parametro.rotulo}
                      ajuda={parametro.ajuda}
                      erro={errosDaReceita[parametro.chave]}
                    >
                      {parametro.tipo === 'hora' ? (
                        <input
                          className="entrada"
                          type="time"
                          value={edicao.valores[parametro.chave] ?? '09:00'}
                          onChange={(evento) =>
                            alterarValor(receita.id, parametro.chave, evento.target.value)
                          }
                        />
                      ) : (
                        <input
                          className="entrada"
                          type="number"
                          inputMode="numeric"
                          min={parametro.minimo}
                          max={parametro.maximo}
                          step={1}
                          value={edicao.valores[parametro.chave] ?? ''}
                          onChange={(evento) =>
                            alterarValor(receita.id, parametro.chave, evento.target.value)
                          }
                        />
                      )}
                    </Campo>
                  ))}

                  <Campo
                    rotulo="Template da mensagem"
                    ajuda={
                      receita.exigeTemplate
                        ? 'Obrigatório para cobrança: é o texto aprovado que sai no WhatsApp.'
                        : 'Opcional. Sem template, o motor usa o texto padrão da receita.'
                    }
                    erro={errosDaReceita.templateId}
                  >
                    <select
                      className="entrada"
                      value={edicao.templateId ?? ''}
                      onChange={(evento) =>
                        alterar(receita.id, { templateId: evento.target.value || null })
                      }
                    >
                      <option value="">
                        {receita.exigeTemplate ? 'Selecione um template…' : 'Usar o texto padrão'}
                      </option>
                      {listaTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.nome}
                          {template.metaTemplateId ? ' · aprovado Meta' : ' · texto livre'}
                        </option>
                      ))}
                    </select>
                  </Campo>

                  <Campo
                    rotulo="Canal de saída"
                    ajuda="O canal define a fila, a vazão e a apiKey daquele Provedor."
                  >
                    <select
                      className="entrada"
                      value={edicao.canalId ?? ''}
                      onChange={(evento) =>
                        alterar(receita.id, { canalId: evento.target.value || null })
                      }
                    >
                      <option value="">Canal padrão do Provedor</option>
                      {listaCanais.map((canal) => (
                        <option key={canal.id} value={canal.id}>
                          {canal.nome} · fila #{canal.xmaxQueueId}
                          {canal.ativo ? '' : ' (inativo)'}
                        </option>
                      ))}
                    </select>
                  </Campo>
                </div>

                {listaTemplates.length === 0 && receita.exigeTemplate ? (
                  <Aviso tipo="alerta" titulo="Nenhum template cadastrado">
                    A receita não pode ficar ligada sem template. Crie um na aba Templates.
                  </Aviso>
                ) : null}

                <div className="linha linha--separada">
                  <span className="texto-pequeno">
                    {receita.atualizadoEm
                      ? `Salva em ${formatarDataHora(receita.atualizadoEm, provedor?.fuso ?? 'America/Sao_Paulo')}`
                      : 'Ainda não configurada neste Provedor'}
                  </span>
                  <button
                    type="button"
                    className="botao botao--primario"
                    onClick={() => void salvar(receita)}
                    disabled={salvando === receita.id}
                  >
                    {salvando === receita.id ? 'Salvando…' : 'Salvar receita'}
                  </button>
                </div>
              </div>

              <aside className="previa" aria-label={`Prévia da mensagem de ${receita.rotulo}`}>
                <span className="previa__rotulo">Prévia para o Titular</span>
                <div className="previa__bolha">{mensagem}</div>
                <span className="previa__rodape">
                  Dados de exemplo. O envio real usa nome, valor e vencimento da fatura.
                  {templateSelecionado
                    ? ` Template: ${templateSelecionado.nome}.`
                    : ' Texto padrão da receita.'}
                </span>
              </aside>
            </div>
          </article>
        );
      })}
    </>
  );
}
